import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { parseSpaBooleanField } from "../../../constants/SpaBooleanFields";

const SpaGuestTypes = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [guestTypeCode, setGuestTypeCode] = useState<string | null>(null);
  const [guestTypeName, setGuestTypeName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [codeFieldError, setCodeFieldError] = useState<boolean>(false);
  const [codeFieldErrorMessage, setCodeFieldErrorMessage] =
    useState<string>("");
  const [nameFieldError, setNameFieldError] = useState<boolean>(false);
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");

  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateGuestTypeFields = (): boolean => {
    if (!guestTypeCode || guestTypeCode.length === 0) {
      setAlertMessage("Guest Type Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCodeFieldError(false);
    setCodeFieldErrorMessage("");
    if (
      !validateString(
        guestTypeCode,
        validationLength.spaGuestTypeCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(true);
      setCodeFieldErrorMessage(validationMessages.spaGuestTypeCode);
      return false;
    }

    if (!guestTypeName || guestTypeName.length === 0) {
      setAlertMessage("Guest Type Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameFieldError(false);
    setNameFieldErrorMessage("");
    if (
      !validateString(
        guestTypeName,
        validationLength.spaGuestTypeName,
        validationType.NAME,
      )
    ) {
      setNameFieldError(true);
      setNameFieldErrorMessage(validationMessages.spaGuestTypeName);
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(
        listForDuplicate,
        DNDFieldsEnum.SPAGUESTTYPECODE,
        guestTypeCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validateGuestTypeFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.guestTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateGuestTypeFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setGuestTypeCode(list["Guest Type Code"] ?? null);
      setGuestTypeName(list["Guest Type Name"] ?? null);
      setActive(parseSpaBooleanField(list?.Active));
    } else {
      setGuestTypeCode(null);
      setGuestTypeName(null);
      setActive(true);
      setCodeFieldError(false);
      setCodeFieldErrorMessage("");
      setNameFieldError(false);
      setNameFieldErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Guest Type Code": guestTypeCode,
    "Guest Type Name": guestTypeName,
    Active: active ? "Yes" : "No",
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Guest Types
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.guestTypeHeader ?? "Guest Types",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Guest Type Code</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.guestTypeGuestTypeCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>{" "}
            <TextField
              variant="outlined"
              placeholder="Guest Type Code"
              value={guestTypeCode ?? ""}
              error={codeFieldError}
              helperText={codeFieldError ? codeFieldErrorMessage : ""}
              onChange={(e) => setGuestTypeCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Guest Type Name</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.guestTypeGuestTypeDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>{" "}
            <TextField
              variant="outlined"
              placeholder="Guest Type Name"
              value={guestTypeName ?? ""}
              onChange={(e) => setGuestTypeName(e.target.value || null)}
              fullWidth
              helperText={nameFieldError ? nameFieldErrorMessage : ""}
              error={nameFieldError}
            />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active"
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Guest Type
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Guest Types Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.SPAGUESTTYPECODE}
              name={DNDFieldsEnum.SPAGUESTTYPENAME}
            />
          ) : (
            <Typography variant="body2">No Guest Types Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaGuestTypes;
